// useLayoutEditorPageState.js

import { useCallback, useMemo, useState } from 'react';

// Utilities
import { getPageViewTypeMetadata } from './layoutPageViewConfig';

function normalizeString(value) {
	return String(value || '').trim();
}

/**
 * createEmptyPageViewDraft
 *
 * Creates a fresh empty page view draft state object.
 *
 * @returns {object} The empty draft state.
 */
function createEmptyPageViewDraft() {
	return {
		relatedTypeKey: '',
		viewType: '',
	};
}

/**
 * useLayoutEditorPageState
 *
 * Owns the page-specific view-block picker state and preview item modal state used by LayoutEditor.
 *
 * @param {function|null} [dismissMenu=null] - Optional callback to dismiss any open slot menu before opening page flows.
 * @param {function|null} [getType=null] - Optional node type lookup used to resolve preview modal item types.
 * @returns {object} Page-specific editor state, derived values, and handlers.
 */
const useLayoutEditorPageState = ({
	dismissMenu = null,
	getType = null,
} = {}) => {
	const [pageViewTypeModalOpen, setPageViewTypeModalOpen] = useState(false);
	const [pageViewDraft, setPageViewDraft] = useState(() => createEmptyPageViewDraft());
	const [viewBlockModalOpen, setViewBlockModalOpen] = useState(false);
	const [viewBlockSlotKey, setViewBlockSlotKey] = useState('');
	const [viewBlockModalIntent, setViewBlockModalIntent] = useState('saved');
	const [pagePreviewSelectedItem, setPagePreviewSelectedItem] = useState(null);

	const resolvedPageViewDraftViewType = normalizeString(pageViewDraft?.viewType).toUpperCase();
	const resolvedPageViewDraftTypeKey = normalizeString(pageViewDraft?.relatedTypeKey);
	const pageViewTypeMetadata = useMemo(() => (
		getPageViewTypeMetadata(resolvedPageViewDraftViewType)
	), [resolvedPageViewDraftViewType]);

	/**
	 * resetPageViewDraft
	 *
	 * Clears the active page view draft back to its initial state.
	 */
	const resetPageViewDraft = useCallback(() => {
		setPageViewDraft(createEmptyPageViewDraft());
	}, []);

	/**
	 * handleOpenPagePreviewItem
	 *
	 * Opens the shared preview item modal for the active page view block.
	 *
	 * @param {object|null} node - Clicked node row.
	 * @param {object|null} view - Source view definition for the click.
	 * @param {object|null} typeDef - Source type definition for the click.
	 */
	const handleOpenPagePreviewItem = useCallback((node, view, typeDef) => {
		if (!node) return;

		setPagePreviewSelectedItem({
			node,
			typeDef: typeDef || getType?.(node?.type) || null,
			view: view || null,
		});
	}, [getType]);

	/**
	 * handleClosePagePreviewItem
	 *
	 * Closes the shared preview item modal.
	 */
	const handleClosePagePreviewItem = useCallback(() => {
		setPagePreviewSelectedItem(null);
	}, []);

	/**
	 * handleCloseViewBlockModal
	 *
	 * Closes the view-block definition modal and clears any pending page draft.
	 */
	const handleCloseViewBlockModal = useCallback(() => {
		setPageViewTypeModalOpen(false);
		resetPageViewDraft();
		setViewBlockModalOpen(false);
		setViewBlockSlotKey('');
	}, [resetPageViewDraft]);

	/**
	 * handleClosePageViewTypeModal
	 *
	 * Closes the page related-type picker and clears its pending draft.
	 */
	const handleClosePageViewTypeModal = useCallback(() => {
		setPageViewTypeModalOpen(false);
		resetPageViewDraft();
		setViewBlockSlotKey('');
	}, [resetPageViewDraft]);

	/**
	 * handleOpenViewBlockModal
	 *
	 * Opens the view-block editor for the requested slot.
	 *
	 * @param {string} slotKey - Target slot key.
	 */
	const handleOpenViewBlockModal = useCallback((slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		dismissMenu?.();
		setPageViewTypeModalOpen(false);
		resetPageViewDraft();
		setViewBlockModalIntent('saved');
		setViewBlockSlotKey(cleanedSlotKey);
		setViewBlockModalOpen(true);
	}, [dismissMenu, resetPageViewDraft]);

	const handleOpenDashboardSavedViewModal = useCallback((slotKey) => {
		handleOpenViewBlockModal(slotKey);
	}, [handleOpenViewBlockModal]);

	const handleOpenDashboardNewViewModal = useCallback((slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		dismissMenu?.();
		setViewBlockModalIntent('create');
		resetPageViewDraft();
		setViewBlockSlotKey(cleanedSlotKey);
		setPageViewTypeModalOpen(true);
	}, [dismissMenu, resetPageViewDraft]);

	/**
	 * handleOpenPageViewTypeModal
	 *
	 * Opens the page related-type picker for a requested page-safe view type.
	 *
	 * @param {string} slotKey - Target slot key.
	 * @param {string} [viewType='LIST'] - Requested page-safe view type.
	 */
	const handleOpenPageViewTypeModal = useCallback((slotKey, viewType = '') => {
		const cleanedSlotKey = normalizeString(slotKey);
		const cleanedViewType = normalizeString(viewType).toUpperCase();
		if (!cleanedSlotKey) return;

		dismissMenu?.();
		setViewBlockModalIntent('create');
		setPageViewDraft({
			relatedTypeKey: '',
			viewType: cleanedViewType,
		});
		setViewBlockSlotKey(cleanedSlotKey);
		setPageViewTypeModalOpen(true);
	}, [dismissMenu]);

	/**
	 * handleSelectPageViewType
	 *
	 * Persists the selected related type into the pending page draft and advances to the view modal.
	 *
	 * @param {string} relatedTypeKey - Selected related type key.
	 */
	const handleSelectPageViewType = useCallback((relatedTypeKey) => {
		const cleanedRelatedTypeKey = normalizeString(relatedTypeKey);
		if (!cleanedRelatedTypeKey) return;

		setPageViewDraft((current) => ({
			relatedTypeKey: cleanedRelatedTypeKey,
			viewType: normalizeString(current?.viewType).toUpperCase()
				|| normalizeString(getType?.(cleanedRelatedTypeKey)?.defaultView).toUpperCase()
				|| 'LIST',
		}));
		setPageViewTypeModalOpen(false);
		setViewBlockModalOpen(true);
	}, [getType]);

	return {
		handleClosePagePreviewItem,
		handleClosePageViewTypeModal,
		handleCloseViewBlockModal,
		handleOpenDashboardNewViewModal,
		handleOpenDashboardSavedViewModal,
		handleOpenPagePreviewItem,
		handleOpenPageViewTypeModal,
		handleOpenViewBlockModal,
		handleSelectPageViewType,
		pagePreviewSelectedItem,
		pageViewTypeModalNoun: pageViewTypeMetadata.noun,
		pageViewTypeModalOpen,
		pageViewTypeModalTitle: pageViewTypeMetadata.title,
		resolvedPageViewDraftTypeKey,
		resolvedPageViewDraftViewType,
		viewBlockModalIntent,
		viewBlockModalOpen,
		viewBlockSlotKey,
	};
};

export default useLayoutEditorPageState;